import DtcAPI from 'api/dtc.api';
import { getAllData, getAllGroupedData, setLoading } from 'store/dtc.slice';
import type { DTC } from 'types/dtc.types';

import { BaseHandler } from './base.handler';
import { TimedSessionCacheStorage } from 'utils/cache/cache';
import { store } from 'store';

export default class DtcHandler extends BaseHandler {
  private readonly api: DtcAPI;
  private readonly cache: TimedSessionCacheStorage; // cache
  private readonly customer = store.getState().persistedReducer.customer.code;

  constructor() {
    super();

    this.api = new DtcAPI();
    this.cache = new TimedSessionCacheStorage(`dtc_${this.customer}`);
  }

  async get(): Promise<DTC[]> {
    try {
      const cacheKey = `dtc_cache`;
      const cachedData = sessionStorage.getItem(cacheKey);

      const payload = {
        startDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split('T')[0], // 5 days ago
        endDate: new Date().toISOString().split('T')[0], // Current day
      };

      if (cachedData) {
        const data = JSON.parse(cachedData);

        // Check if the cache is stale (older than 24 hours)
        const cacheTimestamp = new Date(data.timestamp);
        const currentTime = new Date();
        const hoursDifference =
          (currentTime.getTime() - cacheTimestamp.getTime()) / (1000 * 60 * 60);

        if (hoursDifference < 24) {
          this.dispatch(getAllGroupedData(data));

          return data;
        }
      }
      this.dispatch(setLoading(true));
      const dtcData = await this.api.getAllGroupedData(payload);
      sessionStorage.setItem(
        cacheKey,
        JSON.stringify({ ...dtcData, timestamp: new Date() })
      );

      this.dispatch(getAllGroupedData(dtcData));

      return dtcData;
    } catch (_) {
      this.dispatch(setLoading(false));
      this.handleError('An error occurred while fetching dtc data.');
    }

    return [];
  }

  async getAllData(payload: {
    startDate: string;
    endDate: string;
  }): Promise<DTC[]> {
    this.dispatch(setLoading(true));
    try {
      const dtcData = await this.api.getAllData(payload);

      this.dispatch(getAllData(dtcData));

      return dtcData;
    } catch (_) {
      this.dispatch(setLoading(false));
      this.handleError('An error occurred while fetching dtc data.');
    }

    return [];
  }

  async getAllGroupedData(payload: {
    startDate: string;
    endDate: string;
  }): Promise<DTC[]> {
    this.dispatch(setLoading(true));

    const cacheKey = `dtcGroupedData_${this.customer}`;

    try {
      const cached = this.cache.get(cacheKey);

      const isValid =
        cached &&
        cached.startDate === payload.startDate &&
        cached.endDate === payload.endDate &&
        Array.isArray(cached.value) &&
        cached.value.length > 0;

      if (isValid) {
        this.dispatch(getAllGroupedData(cached.value));
        return cached.value;
      }

      // Fallback to API call
      const dtcGroupedData = await this.api.getAllGroupedData(payload);

      // validate API response shape
      const isValidApiResponse =
        Array.isArray(dtcGroupedData) && dtcGroupedData.length >= 0;

      if (!isValidApiResponse) {
        throw new Error('Invalid DTC grouped data received');
      }

      // Store the new data with date range
      this.cache.set(cacheKey, {
        startDate: payload.startDate,
        endDate: payload.endDate,
        value: dtcGroupedData,
      });

      this.dispatch(getAllGroupedData(dtcGroupedData));
      return dtcGroupedData;
    } catch (_) {
      this.cache.remove(cacheKey); // remove cache in case api fails
      this.dispatch(setLoading(false));
      this.handleError('An error occurred while fetching dtc data.');
      return [];
    }
  }

  async downloadEngineHoursExcel(
    startDate: string,
    endDate: string,
    format: string
  ): Promise<ArrayBuffer> {
    try {
      const response = await this.api.exportToExcel({
        startDate,
        endDate,
        format,
      });
      return response;
    } catch (error) {
      this.handleError('An error occurred while downloading dtc data.');
      throw error;
    }
  }
}
